import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { LockIcon, TriangleAlertIcon } from 'lucide-react';
import {
  NODE_ACTION_LABELS,
  isFieldEdited,
  isNodeFieldEditable,
  levelLabel,
  statedName,
  nodeIssues,
  type JobIssue,
  type JobIssueSeverity,
  type JobNode,
  type PlatformFieldDef,
} from '@/entities/media-campaign';
import { levelFieldColumns } from '../lib/flatten';
import { isFindingOutOfDate, type PreflightStatus } from '../lib/preflight';
import { platformStatusLabel } from './delivery-labels';
import { SEVERITY_TONE } from './severity-tone';
import { RightDrawer } from './RightDrawer';
import { useTranslation } from 'react-i18next';

const EM_DASH = '—';

const SEVERITY_RANK: Record<JobIssueSeverity, number> = { error: 0, warning: 1, advice: 2 };

function FieldRow({
  def,
  node,
  platform,
  issue,
  preflightStatus,
  isFocused,
  onCommit,
}: {
  def: PlatformFieldDef;
  node: JobNode;
  platform: string;
  issue: JobIssue | undefined;
  preflightStatus: PreflightStatus;
  isFocused: boolean;
  onCommit: (value: string | null) => void;
}) {
  const { t } = useTranslation('projects');
  const field = node.fields.find((entry) => entry.key === def.key);
  const value = field?.value ?? null;
  const editable = isNodeFieldEditable(platform, node, def.key);
  const edited = field !== undefined && isFieldEdited(field);
  const outOfDate = isFindingOutOfDate(preflightStatus, edited);
  // An out-of-date finding must not still tint the row or mark the control
  // invalid: the value it objected to is gone.
  const hasError = issue?.severity === 'error' && !outOfDate;

  const ref = useRef<HTMLDivElement | null>(null);
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => setDraft(value ?? ''), [value]);
  useEffect(() => {
    if (isFocused) ref.current?.scrollIntoView({ block: 'center' });
  }, [isFocused]);

  const commit = () => {
    const next = draft.trim();
    if (next === (value ?? '')) return;
    onCommit(next === '' ? null : next);
  };

  return (
    <div
      ref={ref}
      data-job-field={`${node.id}:${def.key}`}
      className={`flex flex-col gap-1 rounded-lg px-2 py-2 ${
        isFocused ? 'outline outline-2 outline-primary' : ''
      } ${hasError ? 'bg-destructive/5' : ''}`}
    >
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {def.label}
        {def.required && <span className="text-destructive">*</span>}
        {!editable && <LockIcon className="size-3" aria-hidden />}
        {edited && (
          <Badge variant="secondary" roundness="full" className="font-normal">
            {t('deliveryJobReview.jobNodeDrawer.edited')}
          </Badge>
        )}
      </span>

      {!editable ? (
        <span className="text-sm text-muted-foreground">
          {platformStatusLabel(node, def.key) ?? value ?? EM_DASH}
        </span>
      ) : def.kind === 'select' && def.options ? (
        <Select value={value ?? ''} onValueChange={(next) => onCommit(next === '' ? null : next)}>
          <SelectTrigger size="sm" error={hasError} data-testid={`job-field-${def.key}`}>
            <SelectValue placeholder={EM_DASH} />
          </SelectTrigger>
          <SelectContent>
            {def.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          size="sm"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit();
          }}
          aria-invalid={hasError}
          placeholder={
            def.kind === 'tokens' ? t('deliveryJobReview.jobNodeDrawer.commaSeparated') : EM_DASH
          }
          data-testid={`job-field-${def.key}`}
        />
      )}

      {issue && (
        <span
          className={
            outOfDate
              ? 'text-xs text-muted-foreground opacity-70'
              : `text-xs ${SEVERITY_TONE[issue.severity]}`
          }
          data-testid={`job-issue-${def.key}`}
          data-out-of-date={outOfDate || undefined}
        >
          {issue.message}
          {outOfDate && t('deliveryJobReview.jobNodeDrawer.reCheckNeededSuffix')}
        </span>
      )}
    </div>
  );
}

export interface JobNodeDrawerProps {
  node: JobNode | null;
  platform: string;
  /** Field key to scroll to and outline, when opened from an issue. */
  focusField: string | null;
  showIssues: boolean;
  /** Drives the out-of-date treatment on fields edited since the last run. */
  preflightStatus: PreflightStatus;
  onEditField: (nodeId: string, key: string, value: string | null) => void;
  onClose: () => void;
}

/**
 * Everything about one campaign or ad set, on demand.
 *
 * Their fields do not fit the leaf columns and printing them inline turned every
 * band into a wall of chips, so the band stays a one-line summary and the detail
 * lives here — the same side surface the issue list uses, so the review only
 * ever has one drawer to learn.
 */
export function JobNodeDrawer({
  node,
  platform,
  focusField,
  showIssues,
  preflightStatus,
  onEditField,
  onClose,
}: JobNodeDrawerProps) {
  const { t } = useTranslation('projects');
  const columns = node ? levelFieldColumns(platform, node.level) : [];
  // Severity first, because a field row shows one issue and advice must not stand in
  // front of the error that blocks publish. Sorting is stable, so within a severity the
  // order the rules produced survives.
  const issues = (node && showIssues ? [...nodeIssues(platform, node)] : []).sort(
    (a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity],
  );
  const errors = issues.filter((issue) => issue.severity === 'error').length;

  return (
    <RightDrawer
      open={node !== null}
      kicker={node ? levelLabel(platform, node.level) : ''}
      title={node ? (statedName(node) ?? t('deliveryJobReview.jobNodeDrawer.untitled')) : ''}
      ariaLabel={t('deliveryJobReview.jobNodeDrawer.objectDetails')}
      testId="job-node-drawer"
      onClose={onClose}
    >
      {node && (
        <div className="flex flex-col gap-1 p-3">
          <div className="flex flex-wrap items-center gap-2 px-2 pb-2">
            <Badge variant="secondary" roundness="full" className="font-normal">
              {NODE_ACTION_LABELS[node.action]}
            </Badge>
            {node.externalId && (
              <span className="text-xs text-muted-foreground">
                {t('deliveryJobReview.jobNodeDrawer.id', { id: node.externalId })}
              </span>
            )}
            {node.sourceRows.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {t('deliveryJobReview.jobNodeDrawer.row', {
                  rows: node.sourceRows.join(t('deliveryJobReview.jobNodeDrawer.listJoin')),
                })}
              </span>
            )}
            {errors > 0 && (
              <Badge variant="destructive" roundness="full" className="font-normal">
                <TriangleAlertIcon className="size-3" />
                {t('deliveryJobReview.jobNodeDrawer.toFix', { count: errors })}
              </Badge>
            )}
          </div>

          {columns.map((column) => (
            <FieldRow
              key={column.key}
              def={column.def}
              node={node}
              platform={platform}
              issue={issues.find((issue) => issue.field === column.fieldKey)}
              preflightStatus={preflightStatus}
              isFocused={focusField === column.fieldKey}
              onCommit={(value) => onEditField(node.id, column.fieldKey, value)}
            />
          ))}
        </div>
      )}
    </RightDrawer>
  );
}
